import * as React from 'react';
import clsx from 'clsx';
import { ModalShell } from '@/components/ui/ModalShell';
import closeIcon from '@/assets/images/svg/close_icon.svg';
import facebookIcon from '@/assets/images/svg/facebook.svg';
import copyIcon from '@/assets/images/svg/share_copy_icon.svg';
import { useBlobUrl } from '@/hooks/useBlobUrl';
import { ShareImagePreview } from '@/components/share/ShareImagePreview';

interface FacebookShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareText: string;
  copied: boolean;
  onCopy: () => void;
  shareUrl: string;
  imageBlob?: Blob | null;
}

const SHARE_TITLE_GRADIENT_STYLE = {
  backgroundImage: 'linear-gradient(90deg, #1A1036 49.53%, #462C8E 95.55%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const;

/** Modal guiding users to share a message on Facebook with an optional share card image. */
export const FacebookShareModal: React.FC<FacebookShareModalProps> = ({
  isOpen,
  onClose,
  shareText,
  copied,
  onCopy,
  shareUrl,
  imageBlob,
}) => {
  const handleGoToFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener'
    );
  };

  const { url: imageUrl, download: handleDownload } = useBlobUrl(imageBlob ?? null);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      position='responsive-sheet'
      portal
      bodyClassName={clsx(
        'w-full bg-white',
        'rounded-t-20 sm:rounded-20 sm:max-w-[489px]',
        'flex max-h-[90vh] flex-col overflow-y-auto'
      )}
    >
      <div className='flex justify-end p-4'>
        <button
          onClick={onClose}
          className='transition-opacity hover:opacity-70'
          aria-label='Close'
        >
          <img src={closeIcon} alt='Close' className='size-6' />
        </button>
      </div>

      <div className='flex flex-col items-center gap-3 px-6 pb-2'>
        <img src={facebookIcon} alt='Facebook' className='size-16' />
        <span
          className='text-center font-proxima text-24 font-bold leading-[47px]'
          style={SHARE_TITLE_GRADIENT_STYLE}
        >
          Share on Facebook
        </span>
      </div>

      <div className='flex flex-col gap-3 px-6 py-4'>
        <p className='text-center font-sans text-14 leading-21 text-gray-600'>
          {imageUrl
            ? 'Save the image below and attach it to your Facebook post:'
            : 'Facebook doesn’t allow pre-filling text in posts. Copy the text below, then paste it into your Facebook post:'}
        </p>

        <div className='flex flex-col gap-2 rounded-12 bg-purple-25 px-4 py-3'>
          <div className='flex items-start gap-2'>
            <span className='mt-0.5 font-sans text-14 font-bold text-brand-800'>1.</span>
            <span className='font-sans text-13 leading-20 text-brand-800'>
              {imageUrl ? 'Save the image above' : 'Copy the message below'}
            </span>
          </div>
          <div className='flex items-start gap-2'>
            <span className='mt-0.5 font-sans text-14 font-bold text-brand-800'>2.</span>
            <span className='font-sans text-13 leading-20 text-brand-800'>
              Click <strong>Go to Facebook</strong> and{' '}
              {imageUrl ? 'attach the image to your post' : 'paste it into your post'}
            </span>
          </div>
        </div>
      </div>

      {imageUrl && <ShareImagePreview imageUrl={imageUrl} onDownload={handleDownload} />}

      <div className='flex flex-col gap-2 px-6 pb-4'>
        <div
          className={clsx(
            'flex w-full items-start gap-3 rounded-12 border px-4 py-3',
            copied ? 'border-green-400 bg-green-50/50' : 'border-purple-120 bg-white'
          )}
        >
          <p className='min-w-0 flex-1 whitespace-pre-wrap break-words font-sans text-12 leading-18 text-brand-800'>
            {shareText}
          </p>
          <button
            onClick={onCopy}
            className='mt-0.5 shrink-0 transition-opacity hover:opacity-70'
            aria-label='Copy text'
          >
            <img src={copyIcon} alt='Copy' className='size-5' />
          </button>
        </div>
        {copied && (
          <span className='font-sans text-12 font-medium text-green-600'>Copied to clipboard!</span>
        )}
      </div>

      <div className='px-6 pb-8'>
        <button
          onClick={handleGoToFacebook}
          className={clsx(
            'flex h-[50px] w-full items-center justify-center rounded-20',
            'bg-blue-500',
            'font-sans text-16 font-bold uppercase tracking-wide text-white',
            'transition-opacity hover:opacity-90'
          )}
        >
          GO TO FACEBOOK
        </button>
      </div>
    </ModalShell>
  );
};
